import { Design, Rectangle, Segment } from '@thive/fddm-types';
import { Vector3 } from 'three';
import { ICommand } from '@/modules/canvas/commands/ICommand';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { IAppService } from '@/modules/app/service/IAppService';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useCanvasStore } from '@/modules/canvas/store';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { IHistoryService } from '@/modules/canvas/services/history/IHistoryService';
import { REFERENCE_IMAGE_ELEMENT_ID } from '@/modules/referenceImage/constants';
import { METERS_TO_MILLIMETERS_FACTOR } from '@/modules/canvas/factories/element';

import { IOriginService } from './IOriginService';
import { ORIGIN_GROUP_ID } from '../constants';
import { logger } from '@common/logging/logger';

export class OriginServiceImpl implements IOriginService {
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private canvasStore: typeof useCanvasStore;
  private fddmSvc: IFddmService;
  private appSvc: IAppService;
  private historySvc: IHistoryService;

  public constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.canvasStore = canvasStore;
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
    this.appSvc = AppServiceFactory.getInstance().createAppService();
    this.historySvc = HistoryServiceFactory.getInstance().createHistoryService();
  }

  startTranslateLifecycle() {
    // This method is intentionally left empty. The lifecycle is managed by the canvas tool and the translation is applied directly to the design.
  }

  endTranslateLifecycle() {
    const canvas = this.canvasStore.getState().instance;
    if (!canvas) {
      logger.error('Could not use canvas. Not defined.');
    } else {
      const originGroupElement = canvas.getElement(ORIGIN_GROUP_ID);

      if (!originGroupElement || originGroupElement.type !== 'Group') {
        logger.error('Could not use canvas. Not defined.');
      } else {
        canvas.updateElement({ ...originGroupElement, position: new Vector3(0, 0, 0) });
        canvas.deselectAll();
      }
    }

    this.appSvc.resetTool();
  }

  translateDesign(delta: { x: number; y: number }) {
    this.historySvc.batch(() => {
      const { design } = this.floorplanDesignStore.getState();
      const translatedDesign = this.calcTranslatedDesign(design, delta);
      this.fddmSvc.setDesign(translatedDesign);
      this.translateReferenceImage(delta);
    });

    this.endTranslateLifecycle();
  }

  private translateReferenceImage(delta: { x: number; y: number }): void {
    const referenceImage = this.fddmSvc.getReferenceImage();
    if (!referenceImage) {
      return;
    }

    this.fddmSvc.updateReferenceImage({
      ...referenceImage,
      rectangle: {
        ...referenceImage.rectangle,
        centerX: referenceImage.rectangle.centerX + delta.x,
        centerY: referenceImage.rectangle.centerY + delta.y,
      },
    });

    this.trackReferenceImageCanvasMove(delta);
  }

  private trackReferenceImageCanvasMove(delta: { x: number; y: number }): void {
    const canvas = this.canvasStore.getState().instance;
    if (!canvas) {
      return;
    }

    const referenceImageElement = canvas.getElement(REFERENCE_IMAGE_ELEMENT_ID);
    if (!referenceImageElement || referenceImageElement.type !== 'ImageCentered') {
      return;
    }

    const referencePosition = referenceImageElement.position;
    if (!referencePosition) {
      logger.error('Expected reference image element to have a defined position.');
      return;
    }

    const currentPosition = new Vector3(
      referencePosition.x,
      referencePosition.y,
      referencePosition.z,
    );
    const xDeltaInMm = delta.x * METERS_TO_MILLIMETERS_FACTOR;
    const yDeltaInMm = delta.y * METERS_TO_MILLIMETERS_FACTOR;
    const targetPosition = new Vector3(
      currentPosition.x + xDeltaInMm,
      currentPosition.y + yDeltaInMm,
      currentPosition.z,
    );

    const canvasMoveCommand: ICommand = {
      execute: () => {
        const latestElement = canvas.getElement(REFERENCE_IMAGE_ELEMENT_ID);
        if (!latestElement || latestElement.type !== 'ImageCentered') {
          return;
        }

        canvas.updateElement({
          ...latestElement,
          position: targetPosition,
        });
      },
      undo: () => {
        const latestElement = canvas.getElement(REFERENCE_IMAGE_ELEMENT_ID);
        if (!latestElement || latestElement.type !== 'ImageCentered') {
          return;
        }

        canvas.updateElement({
          ...latestElement,
          position: currentPosition,
        });
      },
    };

    canvasMoveCommand.execute();
    this.historySvc.track(canvasMoveCommand);
  }

  private calcTranslatedDesign(design: Design, translation: { x: number; y: number }): Design {
    const {
      areas,
      obstacles,
      flows,
      templates,
      interAreaConnections,
      justInTimeRoutingConfigs,
      logicalSpaces,
    } = design;

    const translated: Design = {
      areas: this.calcTranslatedAreas(areas, translation),
      obstacles: obstacles.map((item) => {
        const updatedItem: typeof item = {
          ...item,
          segments: minItemsArrayUtils.mapAtLeast1(item.segments, (seg) =>
            this.calcTranslatedSegment(seg, translation),
          ),
        };

        return updatedItem;
      }),
      flows,
      templates: {
        lassies: templates.lassies,
        lassieMinis: templates.lassieMinis,
        shuttleRacks: templates.shuttleRacks,
      },
      interAreaConnections,
      justInTimeRoutingConfigs,
      logicalSpaces: logicalSpaces.map((item) => {
        const updated = {
          ...item,
          polygon: {
            ...item.polygon,
            points: item.polygon.points.map((p) => ({
              x: p.x + translation.x,
              y: p.y + translation.y,
            })) as (typeof item)['polygon']['points'],
          },
        };

        return updated;
      }),
    };

    return translated;
  }

  private calcTranslatedAreas(
    areas: Design['areas'],
    translation: { x: number; y: number },
  ): Design['areas'] {
    const translated: Design['areas'] = {
      highwayAreas: areas.highwayAreas.map((item) => {
        const updated: typeof item = {
          ...item,
          segments: minItemsArrayUtils.mapAtLeast1(item.segments, (seg) =>
            this.calcTranslatedSegment(seg, translation),
          ),
        };

        return updated;
      }),
      manualAreas: areas.manualAreas.map((item) => {
        const updated: typeof item = {
          ...item,
          rectangle: this.calcTranslatedRectangle(item.rectangle, translation),
        };

        return updated;
      }),
      rackingAreas: areas.rackingAreas.map((item) => {
        const updated: typeof item = {
          ...item,
          rectangle: this.calcTranslatedRectangle(item.rectangle, translation),
        };

        return updated;
      }),
      singularStationAreas: areas.singularStationAreas.map((item) => {
        const updated: typeof item = {
          ...item,
          rectangle: this.calcTranslatedRectangle(item.rectangle, translation),
        };

        return updated;
      }),
      stationaryAreas: areas.stationaryAreas.map((item) => {
        const updated: typeof item = {
          ...item,
          rectangle: this.calcTranslatedRectangle(item.rectangle, translation),
        };

        return updated;
      }),
    };

    return translated;
  }

  private calcTranslatedRectangle(
    rectangle: Rectangle,
    translation: { x: number; y: number },
  ): Rectangle {
    return {
      ...rectangle,
      centerX: rectangle.centerX + translation.x,
      centerY: rectangle.centerY + translation.y,
    };
  }

  private calcTranslatedSegment(segment: Segment, translation: { x: number; y: number }): Segment {
    return {
      ...segment,
      start: {
        x: segment.start.x + translation.x,
        y: segment.start.y + translation.y,
      },
      end: {
        x: segment.end.x + translation.x,
        y: segment.end.y + translation.y,
      },
    };
  }
}
